import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { trackPageView, trackReportViewed as gtmTrackReportViewed } from '../utils/gtm';
import * as Analytics from '../utils/analytics';
import './ReportPage.css';

// Helper function to check if spec_format only has registered field (excluding empty lists)
const isOnlyRegistered = (specFormat) => {
  if (!specFormat || typeof specFormat !== 'object') return false;
  
  const keys = Object.keys(specFormat).filter(key => {
    const value = specFormat[key];
    // Filter out empty arrays and null/undefined values
    if (Array.isArray(value) && value.length === 0) return false;
    if (value === null || value === undefined || value === '') return false;
    return true;
  });
  
  return keys.length === 1 && keys[0] === 'registered';
};

// Helper function to normalize category names for consistent matching
const normalizeCategory = (category) => {
  if (!category) return null;
  
  // Ensure category is a string (might be an object with {value, type, proper_key})
  let categoryStr = category;
  if (typeof category === 'object' && category.value !== undefined) {
    categoryStr = category.value;
  }
  
  // If still not a string, return null
  if (typeof categoryStr !== 'string') {
    return null;
  }
  
  // Convert to lowercase and handle common variations
  const normalized = categoryStr.toLowerCase()
    .replace(/\s*&\s*/g, '-')  // "Health & Fitness" -> "health-fitness"
    .replace(/\s+/g, '-')      // "social media" -> "social-media"
    .trim();
  
  return normalized;
};

// Helper function to determine category from module name
const categorizeModule = (moduleName) => {
  const lowerModule = moduleName.toLowerCase();
  
  // Social Media
  if (['twitter', 'instagram', 'facebook', 'linkedin', 'tiktok', 'snapchat', 'reddit', 'mastodon', 'tumblr'].some(s => lowerModule.includes(s))) {
    return 'social-media';
  }
  // Gaming
  if (['steam', 'xbox', 'playstation', 'epic', 'nintendo', 'twitch', 'discord', 'battlenet', 'origin'].some(s => lowerModule.includes(s))) {
    return 'gaming';
  }
  // Technology (general tech platforms, not specifically dev/coding)
  if (['apple', 'google', 'microsoft', 'meta', 'tech', 'android', 'ios'].some(s => lowerModule.includes(s))) {
    return 'technology';
  }
  // Shopping
  if (['amazon', 'ebay', 'etsy', 'shopify', 'walmart', 'target', 'shop'].some(s => lowerModule.includes(s))) {
    return 'shopping';
  }
  // Productivity
  if (['slack', 'trello', 'notion', 'asana', 'jira', 'monday', 'basecamp', 'clickup'].some(s => lowerModule.includes(s))) {
    return 'productivity';
  }
  // Education
  if (['coursera', 'udemy', 'edx', 'khan', 'skillshare', 'learning', 'academy', 'university', 'college'].some(s => lowerModule.includes(s))) {
    return 'education';
  }
  // Health & Fitness
  if (['strava', 'fitbit', 'myfitnesspal', 'peloton', 'fitness', 'health', 'gym'].some(s => lowerModule.includes(s))) {
    return 'health-fitness';
  }
  // News (separated from blogs) - includes sports and entertainment news
  if (['news', 'times', 'cnn', 'bbc', 'reuters', 'bloomberg', 'techcrunch', 'verge', 'wired', 'espn', 'fox', 'nbc', 'abc', 'cbs', 'tmz', 'variety', 'hollywood reporter'].some(s => lowerModule.includes(s))) {
    return 'news';
  }
  // Blogs
  if (['medium', 'wordpress', 'blogger', 'blog', 'substack', 'ghost'].some(s => lowerModule.includes(s))) {
    return 'blogs';
  }
  // Development
  if (['github', 'gitlab', 'bitbucket', 'codepen', 'replit', 'glitch', 'stackoverflow', 'stack overflow', 'dev.to', 'hackernoon', 'leetcode', 'hackerrank', 'codewars'].some(s => lowerModule.includes(s))) {
    return 'development';
  }
  // Freelance
  if (['fiverr', 'upwork', 'freelancer', 'toptal', '99designs', 'guru', 'peopleperhour', 'flex', 'remote'].some(s => lowerModule.includes(s))) {
    return 'freelance';
  }
  
  return 'others';
};

// Helper function to format date to readable format
const formatDate = (isoDate) => {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch {
    return isoDate;
  }
};

// Helper function to extract value from field (handles both string and object formats)
const extractValue = (field) => {
  if (field === null || field === undefined) return null;
  
  // If it's an object with 'value' property, extract it
  if (typeof field === 'object' && field.value !== undefined) {
    return field.value;
  }
  
  // Otherwise return as-is
  return field;
};

// Helper function to process spec_format into displayable data (priority fields only)
const processSpecFormat = (specFormat) => {
  if (!specFormat) return {};
  
  const displayData = {};
  
  // Priority fields to display (limit to avoid clutter)
  const priorityFields = [
    'name', 'username', 'email', 'phone', 'location', 
    'followers', 'following', 'bio', 'website', 
    'verified', 'premium', 'last_seen', 'creation_date'
  ];
  
  priorityFields.forEach(field => {
    if (specFormat[field] !== undefined && specFormat[field] !== null && specFormat[field] !== '') {
      let value = extractValue(specFormat[field]);
      
      // Skip if value is still null/undefined/empty after extraction
      if (value === null || value === undefined || value === '') return;
      
      // Format dates
      if (['last_seen', 'creation_date'].includes(field)) {
        value = formatDate(value);
      }
      
      // Format booleans
      if (typeof value === 'boolean') {
        value = value ? 'Yes' : 'No';
      }
      
      // Format field names
      const displayKey = field.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      displayData[displayKey] = String(value); // Ensure it's a string for rendering
    }
  });
  
  return displayData;
};

// Helper function to process ALL fields from spec_format for expanded view
const processAllFields = (specFormat) => {
  if (!specFormat) return {};
  
  const displayData = {};
  
  // Skip internal fields
  const skipFields = ['registered', 'platform_variables'];
  
  Object.keys(specFormat).forEach(field => {
    if (skipFields.includes(field)) return;
    
    let value = extractValue(specFormat[field]);
    
    // Skip if value is empty
    if (value === null || value === undefined || value === '' || 
        (Array.isArray(value) && value.length === 0)) return;
    
    // Format dates (ISO 8601 strings)
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      value = formatDate(value);
    }
    
    // Format booleans
    if (typeof value === 'boolean') {
      value = value ? 'Yes' : 'No';
    }
    
    // Format arrays
    if (Array.isArray(value)) {
      value = value.join(', ');
    }
    
    // Format field names
    const displayKey = field.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    
    displayData[displayKey] = String(value);
  });
  
  return displayData;
};

// Helper function to determine breach severity
const determineSeverity = (tags) => {
  if (!tags || !Array.isArray(tags)) return 'minor';
  
  const tagList = tags.map(t => typeof t === 'string' ? t.toLowerCase() : (t.tag || '').toLowerCase());
  
  // Check for passwords
  const hasPasswords = tagList.some(tag => tag.includes('password'));
  
  // Check for physical addresses (exclude email addresses and IP addresses)
  const hasPhysicalAddress = tagList.some(tag => 
    (tag.includes('physical address') || 
     tag.includes('physical addresses') ||
     (tag === 'addresses') || // Exact match only
     tag.includes('home address') ||
     tag.includes('mailing address')) &&
    !tag.includes('email') &&
    !tag.includes('ip')
  );
  
  // Check for SSN
  const hasSSN = tagList.some(tag => 
    tag.includes('ssn') || 
    tag.includes('social security') ||
    tag.includes('national id') ||
    tag.includes('tax id')
  );
  
  // Check for IP addresses
  const hasIP = tagList.some(tag => 
    tag.includes('ip address') || 
    tag.includes('ip addresses')
  );
  
  // Critical: Passwords + (Physical Address OR SSN OR IP)
  if (hasPasswords && (hasPhysicalAddress || hasSSN || hasIP)) {
    return 'critical';
  } 
  // Password Leaked: Passwords only (without critical data)
  else if (hasPasswords) {
    return 'password-leaked';
  } 
  // Moderate: Physical Address or IP (without passwords)
  else if (hasPhysicalAddress || hasIP) {
    return 'moderate';
  } 
  // Minor: Anything else
  else {
    return 'minor';
  }
};

// Helper function to get Bootstrap icon for module (used for both results feed and confirmed accounts)
const getModuleIcon = (moduleName, providedCategory = null) => {
  const lowerModule = moduleName.toLowerCase();
  
  // Comprehensive platform mappings (check these first before falling back to category)
  const iconMap = {
    // Major Tech Companies
    'apple': 'bi-apple',
    'google': 'bi-google',
    'microsoft': 'bi-microsoft',
    'meta': 'bi-meta',
    
    // Social Media
    'discord': 'bi-discord',
    'facebook': 'bi-facebook',
    'twitter': 'bi-twitter',
    'x.com': 'bi-twitter-x',
    'instagram': 'bi-instagram',
    'linkedin': 'bi-linkedin',
    'reddit': 'bi-reddit',
    'snapchat': 'bi-snapchat',
    'tiktok': 'bi-tiktok',
    'youtube': 'bi-youtube',
    'whatsapp': 'bi-whatsapp',
    'telegram': 'bi-telegram',
    'mastodon': 'bi-mastodon',
    'spotify': 'bi-spotify',
    'pinterest': 'bi-pinterest',
    'vimeo': 'bi-vimeo',
    
    // Technology & Development
    'github': 'bi-github',
    'gitlab': 'bi-git',
    'stack overflow': 'bi-stack-overflow',
    'stackoverflow': 'bi-stack-overflow',
    'bitbucket': 'bi-git',
    'codepen': 'bi-code-square',
    'dev.to': 'bi-code-slash',
    
    // Gaming
    'steam': 'bi-steam',
    'playstation': 'bi-playstation',
    'xbox': 'bi-xbox',
    'nintendo': 'bi-nintendo-switch',
    'twitch': 'bi-twitch',
    'epic': 'bi-controller',
    'origin': 'bi-controller',
    'battlenet': 'bi-controller',
    
    // Productivity & Work
    'slack': 'bi-slack',
    'trello': 'bi-trello',
    'notion': 'bi-journal-text',
    'asana': 'bi-kanban',
    'jira': 'bi-diagram-3',
    'monday': 'bi-calendar-check',
    'dropbox': 'bi-dropbox',
    'box': 'bi-box',
    'evernote': 'bi-journal-bookmark',
    
    // Shopping & Commerce
    'amazon': 'bi-amazon',
    'ebay': 'bi-shop',
    'shopify': 'bi-shop-window',
    'paypal': 'bi-paypal',
    'stripe': 'bi-credit-card',
    'venmo': 'bi-cash',
    
    // Communication & Email
    'gmail': 'bi-envelope-at',
    'outlook': 'bi-microsoft',
    'yahoo': 'bi-envelope',
    'protonmail': 'bi-shield-lock',
    'mailchimp': 'bi-envelope-paper',
    'zoom': 'bi-camera-video',
    'skype': 'bi-skype',
    
    // Blogging & Content
    'wordpress': 'bi-wordpress',
    'medium': 'bi-medium',
    'blogger': 'bi-pen',
    'substack': 'bi-envelope-paper',
    'wix': 'bi-brush',
    'squarespace': 'bi-layout-text-window',
    
    // News & Media (including sports and entertainment)
    'news': 'bi-newspaper',
    'times': 'bi-newspaper',
    'cnn': 'bi-newspaper',
    'bbc': 'bi-newspaper',
    'techcrunch': 'bi-newspaper',
    'nytimes': 'bi-newspaper',
    'business insider': 'bi-newspaper',
    'insider': 'bi-newspaper',
    'bloomberg': 'bi-newspaper',
    'wsj': 'bi-newspaper',
    'wall street': 'bi-newspaper',
    'espn': 'bi-newspaper',
    'fox': 'bi-newspaper',
    'nbc': 'bi-newspaper',
    'abc': 'bi-newspaper',
    'cbs': 'bi-newspaper',
    'tmz': 'bi-newspaper',
    'variety': 'bi-newspaper',
    'entertainment': 'bi-film',
    'disney': 'bi-film',
    'netflix': 'bi-tv',
    'hulu': 'bi-tv',
    'hbo': 'bi-tv',
    
    // Education & Learning
    'coursera': 'bi-mortarboard-fill',
    'udemy': 'bi-book',
    'edx': 'bi-mortarboard-fill',
    'khan': 'bi-mortarboard-fill',
    'skillshare': 'bi-pencil-square',
    'duolingo': 'bi-translate',
    'codecademy': 'bi-code-slash',
    
    // Health & Fitness
    'strava': 'bi-activity',
    'fitbit': 'bi-heart-pulse',
    'peloton': 'bi-bicycle',
    'myfitnesspal': 'bi-heart-pulse',
    'healthkit': 'bi-heart',
    
    // Finance & Banking
    'bank': 'bi-bank',
    'robinhood': 'bi-graph-up',
    'coinbase': 'bi-currency-bitcoin',
    'cash app': 'bi-cash-coin',
    
    // Travel
    'airbnb': 'bi-house',
    'booking': 'bi-calendar-check',
    'uber': 'bi-car-front',
    'lyft': 'bi-taxi-front',
    
    // Cloud & Storage
    'icloud': 'bi-cloud',
    'onedrive': 'bi-cloud-arrow-up',
    'drive': 'bi-cloud-upload',
    
    // Development & Coding
    'codepen': 'bi-code-square',
    'replit': 'bi-terminal',
    'glitch': 'bi-stars',
    'leetcode': 'bi-code-slash',
    'hackerrank': 'bi-code-slash',
    'codewars': 'bi-trophy',
    'hackernoon': 'bi-code',
    
    // Freelance Platforms
    'fiverr': 'bi-briefcase',
    'upwork': 'bi-briefcase',
    'freelancer': 'bi-person-badge',
    'toptal': 'bi-award',
    '99designs': 'bi-palette',
    'guru': 'bi-mortarboard'
  };
  
  // Try exact match first (check all specific platforms)
  for (const [key, icon] of Object.entries(iconMap)) {
    if (lowerModule.includes(key)) {
      // Found specific platform match, return immediately
      return icon;
    }
  }
  
  // Only after checking ALL specific platforms, fall back to category-based icons
  // Use providedCategory if available (from Firestore), otherwise categorize based on module name
  // Normalize category for consistent matching
  const category = providedCategory ? normalizeCategory(providedCategory) : categorizeModule(moduleName);
  const categoryIcons = {
    'social-media': 'bi-people',
    'technology': 'bi-cpu',
    'gaming': 'bi-controller',
    'blogs': 'bi-file-text',
    'news': 'bi-newspaper',
    'shopping': 'bi-cart',
    'education': 'bi-mortarboard-fill',
    'health-fitness': 'bi-heart-pulse',
    'productivity': 'bi-kanban',
    'development': 'bi-code-slash',
    'freelance': 'bi-briefcase',
    'others': 'bi-globe'
  };
  
  // Return category icon, or globe as final fallback
  return categoryIcons[category] || 'bi-globe';
};

function ReportPage() {
  const { reportId } = useParams();
  const [status, setStatus] = useState('created');
  const [initialQuery, setInitialQuery] = useState(null);
  const [selectors, setSelectors] = useState({
    usernames: [],
    names: [],
    emails: [],
    phones: []
  });
  const [confirmedAccounts, setConfirmedAccounts] = useState([]);
  const [breaches, setBreaches] = useState([]);
  const [results, setResults] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [expandedResult, setExpandedResult] = useState(null);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  
  // Track page view on mount
  useEffect(() => {
    trackPageView(`/report/${reportId}`, `Report ${reportId} - OSIN7`);
  }, [reportId]);

  // Copy to clipboard function
  const copyToClipboard = (text, fieldId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  useEffect(() => {
    if (!reportId) return;

    const reportRef = doc(db, 'reports', reportId);
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(
      reportRef,
      (docSnapshot) => {
        if (!docSnapshot.exists()) {
          setError('Report not found');
          return;
        }

        const data = docSnapshot.data();
        const reportStatus = data.status || 'created';
        setStatus(reportStatus);

        // Extract initial query/selector
        if (data.query) {
          setInitialQuery(data.query);
        } else if (data.results && data.results.length > 0 && data.results[0].query) {
          // Fallback: get query from first result if not at report level
          setInitialQuery(data.results[0].query);
        }

        // Check if job is complete
        if (reportStatus === 'complete' || reportStatus === 'hop1_complete') {
          setIsComplete(true);
          
          // Set AI summary if available
          if (data.ai_summary) {
            setAiSummary(data.ai_summary);
          }
        }

        // Process results
        if (data.results && Array.isArray(data.results)) {
          const newSelectors = {
            usernames: new Set(),
            names: new Set(),
            emails: new Set(),
            phones: new Set()
          };
          const newConfirmedAccounts = [];
          const newBreaches = [];
          const newResults = [];

          data.results.forEach((result, resultIndex) => {
            const module = result.module || 'Unknown';
            // Use category from Firestore if available, otherwise categorize based on module name
            // Extract category.name from object structure: {name: "Technology", description: "..."}
            const rawCategory = result.category?.name || null;
            // Normalize category for consistent matching (handles "Gaming", "Health & Fitness", etc.)
            const category = rawCategory ? normalizeCategory(rawCategory) : categorizeModule(module);
            
            // Check if it's a breach (hibp)
            if (module.toLowerCase() === 'hibp') {
              // Process breach data
              if (result.spec_format && Array.isArray(result.spec_format)) {
                result.spec_format.forEach((spec, specIndex) => {
                  const breachValue = extractValue(spec.breach);
                  if (breachValue) {
                    // Extract breach info from spec fields and platform_variables
                    const breachName = extractValue(spec.name) || module;
                    const breachLogo = extractValue(spec.picture_url) || '';
                    
                    // Extract data from platform_variables array
                    let breachDate = extractValue(spec.creation_date) || 'Unknown';
                    let exposedData = [];
                    
                    if (spec.platform_variables && Array.isArray(spec.platform_variables)) {
                      // Find breach_date in platform_variables
                      const breachDateObj = spec.platform_variables.find(item => item.key === 'breach_date');
                      if (breachDateObj) {
                        breachDate = breachDateObj.value;
                      }
                      
                      // Find data_classes in platform_variables (actual exposed data)
                      const dataClassesObj = spec.platform_variables.find(item => item.key === 'data_classes');
                      if (dataClassesObj && Array.isArray(dataClassesObj.value)) {
                        exposedData = dataClassesObj.value;
                      }
                    }

                    newBreaches.push({
                      id: `breach-${resultIndex}-${specIndex}`,
                      service: String(breachName),
                      logo: String(breachLogo),
                      severity: determineSeverity(exposedData.map(tag => ({ tag }))),
                      date: formatDate(breachDate),
                      exposed: exposedData
                    });
                  }
                });
              }
            } else {
              // Process regular results
              if (result.spec_format && Array.isArray(result.spec_format)) {
                result.spec_format.forEach((spec, specIndex) => {
                  // Extract selectors from spec_format (handling both string and object formats)
                  // Emails and email hints
                  if (spec.email) {
                    const emailValue = extractValue(spec.email);
                    if (emailValue) {
                      const emails = String(emailValue).split(',').map(e => e.trim());
                      emails.forEach(email => {
                        if (email) newSelectors.emails.add(email);
                      });
                    }
                  }
                  if (spec.email_hint) {
                    const emailHintValue = extractValue(spec.email_hint);
                    if (emailHintValue) {
                      const emailHints = String(emailHintValue).split(',').map(e => e.trim());
                      emailHints.forEach(hint => {
                        if (hint) newSelectors.emails.add(hint);
                      });
                    }
                  }
                  
                  // Phones and phone hints
                  if (spec.phone) {
                    const phoneValue = extractValue(spec.phone);
                    if (phoneValue) {
                      const phones = String(phoneValue).split(',').map(p => p.trim());
                      phones.forEach(phone => {
                        if (phone) newSelectors.phones.add(phone);
                      });
                    }
                  }
                  if (spec.phone_hint) {
                    const phoneHintValue = extractValue(spec.phone_hint);
                    if (phoneHintValue) {
                      const phoneHints = String(phoneHintValue).split(',').map(p => p.trim());
                      phoneHints.forEach(hint => {
                        if (hint) newSelectors.phones.add(hint);
                      });
                    }
                  }
                  
                  // Usernames
                  if (spec.username) {
                    const usernameValue = extractValue(spec.username);
                    if (usernameValue) {
                      newSelectors.usernames.add(usernameValue);
                    }
                  }
                  
                  // Full names
                  if (spec.name) {
                    const nameValue = extractValue(spec.name);
                    if (nameValue) {
                      newSelectors.names.add(nameValue);
                    }
                  }
                  // Construct name from first_name and last_name
                  if (spec.first_name || spec.last_name) {
                    const firstName = extractValue(spec.first_name) || '';
                    const lastName = extractValue(spec.last_name) || '';
                    const fullName = `${firstName} ${lastName}`.trim();
                    if (fullName) {
                      newSelectors.names.add(fullName);
                    }
                  }

                  // Classify the result
                  if (isOnlyRegistered(spec)) {
                    // It's a confirmed account
                    newConfirmedAccounts.push({
                      id: `account-${resultIndex}-${specIndex}`,
                      platform: module,
                      category: category
                    });
                  } else {
                    // It's a results feed item
                    const displayData = processSpecFormat(spec);
                    const allData = processAllFields(spec);
                    
                    // Only add if there's ANY meaningful data to display
                    // Check both priority fields and all fields
                    if (Object.keys(allData).length > 0) {
                      const pictureUrl = extractValue(spec.picture_url);
                      const verified = extractValue(spec.verified);
                      
                      // If no priority fields, use a subset of all fields
                      const dataToShow = Object.keys(displayData).length > 0 
                        ? displayData 
                        : Object.fromEntries(
                            Object.entries(allData).slice(0, 5) // Show first 5 fields
                          );
                      
                      newResults.push({
                        id: `result-${resultIndex}-${specIndex}`,
                        module: module,
                        category: category, // Use category from Firestore
                        profilePhoto: pictureUrl || null,
                        data: dataToShow,
                        fullData: spec, // Store full spec_format for expanded view
                        confidence: verified ? 'high' : 'medium'
                      });
                    }
                  }
                });
              }
            }
          });

          // Sort breaches by severity (most critical first)
          const severityOrder = {
            'critical': 0,
            'password-leaked': 1,
            'moderate': 2,
            'minor': 3
          };
          
          const sortedBreaches = newBreaches.sort((a, b) => {
            return severityOrder[a.severity] - severityOrder[b.severity];
          });
          
          // Update state with new data - convert Sets to arrays
          setSelectors({
            usernames: Array.from(newSelectors.usernames),
            names: Array.from(newSelectors.names),
            emails: Array.from(newSelectors.emails),
            phones: Array.from(newSelectors.phones)
          });
          setConfirmedAccounts(newConfirmedAccounts);
          setBreaches(sortedBreaches);
          setResults(newResults);
          
          // Track report viewed (only once when results are first loaded)
          if (!hasTrackedView && newResults.length > 0 && reportStatus === 'complete') {
            const tier = data.tier || 'tier1';
            gtmTrackReportViewed(reportId, tier, newResults.length);
            Analytics.trackReportViewed(reportId, tier, newResults.length);
            setHasTrackedView(true);
          }
        }
      },
      (error) => {
        console.error('Error listening to report:', error);
        setError('Failed to load report data');
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [reportId]);

  // Show error state
  if (error) {
    return (
      <div className="report-page">
        <div className="report-container">
          <header className="report-header">
            <Link to="/" className="report-logo-link">
              <h1 className="report-logo">OSIN7</h1>
            </Link>
          </header>
          <div className="error-message">
            <div className="error-icon">⚠️</div>
            <h2>Error Loading Report</h2>
            <p>{error}</p>
            <Link to="/" className="back-home-button">Return Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-page">
      {modalImage && (
        <div className="image-modal" onClick={() => setModalImage(null)}>
          <div className="modal-content">
            <button className="modal-close" onClick={() => setModalImage(null)}>×</button>
            <img src={modalImage} alt="Full size profile" className="modal-image" />
          </div>
        </div>
      )}

      {expandedResult && (
        <div className="expanded-modal" onClick={() => setExpandedResult(null)}>
          <div className="expanded-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="expanded-modal-header">
              <div className="expanded-header-info">
                <i className={`${getModuleIcon(expandedResult.module, expandedResult.category)} expanded-icon`}></i>
                <h2>{expandedResult.module}</h2>
              </div>
              <button className="modal-close" onClick={() => setExpandedResult(null)}>×</button>
            </div>
            
            <div className="expanded-modal-body">
              {expandedResult.profilePhoto && (
                <div className="expanded-profile-section">
                  <img 
                    src={expandedResult.profilePhoto} 
                    alt="Profile" 
                    className="expanded-profile-photo"
                    onClick={() => setModalImage(expandedResult.profilePhoto)}
                  />
                </div>
              )}
              
              <div className="expanded-data-grid">
                {Object.entries(processAllFields(expandedResult.fullData)).map(([key, value]) => (
                  <div key={key} className="expanded-data-row">
                    <span className="expanded-data-key">{key}</span>
                    <span className="expanded-data-value">{value}</span>
                  </div>
                ))}
              </div>

              {expandedResult.fullData?.profile_url && (
                <div className="expanded-footer">
                  <a 
                    href={extractValue(expandedResult.fullData.profile_url)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="expanded-profile-link"
                  >
                    View Full Profile ↗
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="report-container">
        <header className="report-header">
          <Link to="/" className="report-logo-link">
            <h1 className="report-logo">OSIN7</h1>
          </Link>
          <div className="report-status">
            {!isComplete ? (
              <div className="status-streaming">
                <span className="pulse"></span>
                {status === 'created' ? 'Waiting for payment...' : 'Searching for results...'}
              </div>
            ) : (
              <div className="status-complete">
                <i className="bi-check-circle"></i> Search Complete
              </div>
            )}
          </div>
        </header>

        {initialQuery && (
          <div className="initial-query-section">
            <div className="query-label">Report for:</div>
            <div className="query-value">{initialQuery}</div>
          </div>
        )}

        <div className="report-notice">
          <p>
            <i className="bi-link-45deg"></i> You can close this tab. A magic link to this report has been sent to your email.
            <br />
            <i className="bi-clock"></i> This report will be available for 72 hours.
          </p>
        </div>

        {isComplete && aiSummary && (
          <div className="ai-summary-section">
            <div className="ai-summary-cell">
              <div className="ai-icon wireframe-ai"></div>
              <div className="ai-content">
                <h3>AI Summary</h3>
                <p>{aiSummary}</p>
              </div>
            </div>
          </div>
        )}

        {(selectors.usernames.length > 0 || selectors.names.length > 0 || selectors.emails.length > 0 || selectors.phones.length > 0) && (
          <div className="selectors-section">
            <h2>
              <div className="section-icon wireframe-target"></div>
              Discovered Selectors
            </h2>
            
            {selectors.usernames.length > 0 && (
              <div className="selector-category">
                <h3 className="selector-category-title">
                  <i className="bi-person-badge"></i> Usernames
                </h3>
                <div className="selectors-list">
                  {selectors.usernames.map((username, index) => (
                    <div key={`username-${index}`} className="selector-chip">
                      <i className="bi-person selector-bootstrap-icon"></i>
                      <span>{username}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {selectors.names.length > 0 && (
              <div className="selector-category">
                <h3 className="selector-category-title">
                  <i className="bi-person-vcard"></i> Full Names
                </h3>
                <div className="selectors-list">
                  {selectors.names.map((name, index) => (
                    <div key={`name-${index}`} className="selector-chip">
                      <i className="bi-person-circle selector-bootstrap-icon"></i>
                      <span>{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {selectors.emails.length > 0 && (
              <div className="selector-category">
                <h3 className="selector-category-title">
                  <i className="bi-envelope-at"></i> Emails & Hints
                </h3>
                <div className="selectors-list">
                  {selectors.emails.map((email, index) => (
                    <div key={`email-${index}`} className="selector-chip">
                      <i className="bi-envelope selector-bootstrap-icon"></i>
                      <span>{email}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {selectors.phones.length > 0 && (
              <div className="selector-category">
                <h3 className="selector-category-title">
                  <i className="bi-phone"></i> Phone Numbers & Hints
                </h3>
                <div className="selectors-list">
                  {selectors.phones.map((phone, index) => (
                    <div key={`phone-${index}`} className="selector-chip">
                      <i className="bi-telephone selector-bootstrap-icon"></i>
                      <span>{phone}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="results-section">
          <h2>
            <div className="section-icon wireframe-results"></div>
            Results Feed
          </h2>
          <div className="results-feed">
            {results.length === 0 && !isComplete && (
              <div className="results-empty">
                <div className={status === 'created' ? 'loading-spinner-pulse' : 'loading-spinner'}></div>
                {status === 'created' ? (
                  <p>Waiting for payment...</p>
                ) : (
                  <p>Scanning data sources...</p>
                )}
              </div>
            )}

            {results.map((result) => (
              <div key={result.id} className="result-cell">
                <div className="result-cell-header">
                  <div className="platform-info">
                    <i className={`${getModuleIcon(result.module, result.category)} result-category-icon`}></i>
                    <h3>{result.module}</h3>
                  </div>
                  {result.confidence === 'high' && (
                    <div className="confidence-icon" data-tooltip="High Confidence">
                      <i className="bi-check-circle-fill"></i>
                    </div>
                  )}
                </div>
                
                <div className="result-body">
                  {result.profilePhoto && (
                    <div className="profile-photo-container">
                      <img 
                        src={result.profilePhoto} 
                        alt="Profile" 
                        className="profile-photo"
                        onClick={() => setModalImage(result.profilePhoto)}
                      />
                    </div>
                  )}
                  
                  <div className="result-data">
                    {Object.entries(result.data).map(([key, value]) => {
                      const displayValue = value.length > 35 ? value.substring(0, 35) + '...' : value;
                      const fieldId = `${result.id}-${key}`;
                      return (
                        <div key={key} className="data-row">
                          <span className="data-key">{key}</span>
                          <div className="data-value-container">
                            <span className="data-value" title={value}>{displayValue}</span>
                            <button 
                              className="copy-button"
                              onClick={() => copyToClipboard(value, fieldId)}
                              title="Copy to clipboard"
                            >
                              {copiedField === fieldId ? (
                                <i className="bi-check2"></i>
                              ) : (
                                <i className="bi-clipboard"></i>
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="result-footer">
                  {result.fullData?.profile_url && extractValue(result.fullData.profile_url) && (
                    <a 
                      href={extractValue(result.fullData.profile_url)} 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="view-account-link"
                    >
                      View Account ↗
                    </a>
                  )}
                  <button 
                    className="expand-button"
                    onClick={() => setExpandedResult(result)}
                  >
                    Expand Result
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {results.length === 0 && isComplete && (
          <div className="no-results">
            <div className="no-results-icon">😔</div>
            <h2>No Results Found</h2>
            <p>We couldn't find any accounts associated with this selector.</p>
            <button className="refund-button">Request Full Refund</button>
          </div>
        )}

        {confirmedAccounts.length > 0 && (
          <div className="confirmed-accounts-section">
            <h2>
              <div className="section-icon wireframe-check"></div>
              Confirmed Accounts
            </h2>
            <p className="confirmed-accounts-subtitle">
              Your email is confirmed to be registered with the following platforms
            </p>
            <div className="confirmed-accounts-grid">
              {confirmedAccounts.map((account) => (
                <div key={account.id} className="confirmed-account-card">
                  <i className={`${getModuleIcon(account.platform, account.category)} confirmed-account-icon`}></i>
                  <span className="confirmed-platform-name">{account.platform}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {breaches.length > 0 && (
          <div className="breaches-section">
            <h2>
              <div className="section-icon wireframe-breach"></div>
              Data Breaches
            </h2>
            <p className="breaches-subtitle">
              Your email was found in the following data breaches
            </p>
            <div className="breaches-grid">
              {breaches.map((breach) => (
                <div key={breach.id} className="breach-card">
                  <div className="breach-header">
                    <div className="breach-logo">
                      <img src={breach.logo} alt={breach.service} className="breach-logo-img" />
                    </div>
                    <div className="breach-info">
                      <h3>{breach.service}</h3>
                      <span className={`severity-badge severity-${breach.severity}`}>
                        {breach.severity === 'critical' && 'Critical'}
                        {breach.severity === 'password-leaked' && 'Password Leaked'}
                        {breach.severity === 'moderate' && 'Moderate'}
                        {breach.severity === 'minor' && 'Minor'}
                      </span>
                    </div>
                  </div>
                  <div className="breach-details">
                    <div className="breach-date">
                      <span className="detail-label">Breach Date</span>
                      <span className="detail-value">{breach.date}</span>
                    </div>
                    <div className="breach-exposed">
                      <span className="detail-label">Data Exposed</span>
                      <div className="exposed-list">
                        {breach.exposed.map((item, idx) => (
                          <span key={idx} className="exposed-item">{item}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReportPage;

